/**
 * Tiny replaceable timeout handle (debounce / delayed one-shot).
 *
 * Prefer this over hand-rolled `Maybe<ReturnType<typeof setTimeout>>` +
 * clear/set. Not Vue-specific: safe in stores, editor plugins, and TEA Cmd runners.
 */
import { isJust, isNothing, just, nothing, type Maybe } from "./maybe";

export type TimeoutHandle = {
  /** Cancel any armed callback without running it. */
  clear: () => void;
  /** Replace any armed callback; run `fn` after `ms`. */
  schedule: (ms: number, fn: () => void) => void;
  /** If armed, cancel the timer and run the pending callback now. */
  flush: () => void;
  /** Whether a callback is currently armed. */
  isPending: () => boolean;
};

export function createTimeoutHandle(): TimeoutHandle {
  let timer: Maybe<ReturnType<typeof setTimeout>> = nothing;
  let pending: Maybe<() => void> = nothing;

  function clear(): void {
    if (isJust(timer)) clearTimeout(timer.value);
    timer = nothing;
    pending = nothing;
  }

  function schedule(ms: number, fn: () => void): void {
    if (isJust(timer)) clearTimeout(timer.value);
    pending = just(fn);
    timer = just(
      setTimeout(() => {
        timer = nothing;
        const run = pending;
        pending = nothing;
        if (isJust(run)) run.value();
      }, ms),
    );
  }

  function flush(): void {
    if (isNothing(pending)) {
      clear();
      return;
    }
    const run = pending.value;
    clear();
    run();
  }

  function isPending(): boolean {
    return isJust(timer);
  }

  return { clear, schedule, flush, isPending };
}
